import { useCallback, useEffect, useMemo, useState } from 'react';

interface UseChannelDisclosureArgs {
  searchQuery: string;
  matchedChannelIds: number[];
  defaultOpenChannelId?: number;
}

export const useChannelDisclosure = ({
  searchQuery,
  matchedChannelIds,
  defaultOpenChannelId,
}: UseChannelDisclosureArgs) => {
  const [openOverrides, setOpenOverrides] = useState<Record<number, boolean>>({});

  useEffect(() => {
    setOpenOverrides({});
  }, [searchQuery]);

  const isChannelDefaultOpen = useCallback(
    (channelId: number) => {
      if (searchQuery) {
        return matchedChannelIds.includes(channelId);
      }

      return channelId === defaultOpenChannelId;
    },
    [defaultOpenChannelId, matchedChannelIds, searchQuery]
  );

  const isChannelOpen = useCallback(
    (channelId: number) => openOverrides[channelId] ?? isChannelDefaultOpen(channelId),
    [isChannelDefaultOpen, openOverrides]
  );

  const toggleChannelOpen = useCallback(
    (channelId: number) => {
      setOpenOverrides((openOverridesPrev) => ({
        ...openOverridesPrev,
        [channelId]: !(openOverridesPrev[channelId] ?? isChannelDefaultOpen(channelId)),
      }));
    },
    [isChannelDefaultOpen]
  );

  return useMemo(() => ({ isChannelOpen, toggleChannelOpen }), [isChannelOpen, toggleChannelOpen]);
};
